import { Injectable } from "@angular/core";
import { decodeTime } from "ulid";

/**
 * Service for ULID (Universally Unique Lexicographically Sortable Identifier) operations
 * Provides utilities for generating, validating, and extracting info from ULIDs
 */
@Injectable({
  providedIn: "root",
})
export class UlidService {
  /**
   * Extract information from a ULID
   * @param id The ULID to analyze
   * @returns Object with extracted information or null if invalid
   */
  extractInfo(ulid: string): {
    prefix?: string;
    ulid: string;
    timestamp: number;
    date: Date;
  } | null {
    try {
      let prefix: string | undefined;

      const timestamp = decodeTime(ulid);
      const date = new Date(timestamp);

      return {
        prefix,
        ulid: ulid,
        timestamp,
        date,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Get creation date from a ULID
   * @param id The ULID to extract date from
   * @returns Date object or null if invalid
   */
  getDateFromId(id: string): Date | null {
    const info = this.extractInfo(id);
    return info ? info.date : null;
  }

  /**
   * Compare two ULIDs chronologically
   * @param id1 First ULID
   * @param id2 Second ULID
   * @returns -1 if id1 is older, 0 if same time, 1 if id1 is newer
   */
  compare(id1: string, id2: string): number {
    const info1 = this.extractInfo(id1);
    const info2 = this.extractInfo(id2);

    if (!info1 || !info2) {
      return 0;
    }

    return info1.timestamp - info2.timestamp;
  }
}
